import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { toast } from "react-toastify";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import {
  AdminSimAction,
  AdminSimOverview,
  AdminSimSession,
  getDeviceSim,
  getDeviceSimSessions,
  refreshDeviceSim,
  sendDeviceSimSms,
  setDeviceSimState,
} from "@src/services/adminService";
import { formatLastOnline } from "@src/utils/deviceOnline";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

/**
 * Hologram SIM status + actions for the admin device page.
 *
 * Talks to /api/admin/devices/:esn/sim*, which proxies the Hologram REST API
 * (backend services/hologramService.ts). Rendered as the "Hologram" sub-tab of
 * AdminDeviceDetailPage. Admin screens are English-only.
 */

const REFETCH_AFTER_ACTION_MS = 30_000;
const SMS_MAX = 160;

type SimCommand = {
  code: string;
  label: string;
  /** Prefill only — the admin completes the text before sending. */
  editable?: boolean;
  /** Disruptive: ask for confirmation before sending. */
  confirm?: boolean;
};

/** Device firmware SMS commands. The code is sent as the SMS body verbatim. */
const SIM_COMMANDS: SimCommand[] = [
  { code: "01", label: "Wake up" },
  { code: "02", label: "Reboot", confirm: true },
  { code: "0301", label: "Logs send" },
  { code: "07", label: "Factory reset", confirm: true },
  { code: "0801", label: "Latest firmware on the server", confirm: true },
  { code: "0901", label: "HW sleep ON", confirm: true },
  { code: "0902", label: "HW sleep OFF" },
  { code: "0a01", label: "Sensor sleep OFF" },
  { code: "0a02", label: "Sensor sleep ON", confirm: true },
  { code: "0c01686f6c6f6772616d", label: "Set Hologram APN", confirm: true },
  { code: "0d03", label: "Laser sensors regime", confirm: true },
  { code: "0d07", label: "Tenso sensors regime", confirm: true },
  { code: "0d04", label: "CLAAS sensors", confirm: true },
  { code: "0d0c", label: "Retrofit sensors", confirm: true },
  { code: "0f0000", label: "Default inversion of tenso sensors" },
  { code: "0f0001", label: "Invert tenso value" },
  { code: "0f0002", label: "Invert tenso freq" },
  { code: "0f0003", label: "Invert both sensors" },
  { code: "0f0a01", label: "JD CAN antenna priority" },
  { code: "1002", label: "Set up sensor zero", confirm: true },
  { code: "COPS0", label: "Clear network requirements", editable: true, confirm: true },
];

const CUSTOM_COMMAND = "__custom__";

function commandLabel(body: unknown): string | null {
  if (typeof body !== "string") return null;
  const exact = SIM_COMMANDS.find((c) => !c.editable && c.code === body);
  if (exact) return exact.label;
  const prefix = SIM_COMMANDS.find((c) => c.editable && body.startsWith(c.code));
  return prefix ? prefix.label : null;
}

// Same look as DeviceInfoSubTab's InfoCard / SectionTitle.
const Card = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
  }
`;

const CardTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  color: #181b1f;
`;

const Label = styled.span`
  font-size: 11px;
  color: #899093;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 6px;
`;

const StateDot = styled.span<{ dotColor: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${({ dotColor }) => dotColor};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const Muted = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #899093;
  margin-top: 8px;
  line-height: 1.45;
`;

const LinkBtn = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #4a90d9;
  cursor: pointer;
  user-select: none;
  &:hover {
    text-decoration: underline;
  }
`;

const SmsBox = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 480px;

  select,
  textarea {
    font-family: Nunito Sans, sans-serif;
    font-size: 14px;
    padding: 8px;
    border: 1px solid #ebeeef;
    border-radius: 6px;
    resize: vertical;
  }
`;

const TableWrap = styled.div`
  margin-top: 12px;
  overflow-x: auto;

  table {
    border-collapse: collapse;
    font-family: Nunito Sans, sans-serif;
    font-size: 13px;
    white-space: nowrap;
  }
  th {
    text-align: left;
    font-size: 11px;
    font-weight: normal;
    color: #899093;
    text-transform: uppercase;
    padding: 6px 16px 6px 0;
    border-bottom: 1px solid #ebeeef;
  }
  td {
    padding: 6px 16px 6px 0;
    border-bottom: 1px solid #f5f5f5;
    color: #181b1f;
  }
`;

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return "-";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

// Hologram timestamps are "YYYY-MM-DD HH:mm:ss" in UTC.
function parseUtc(ts: string | null): Date | null {
  if (!ts) return null;
  const d = new Date(ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTs(ts: string | null): string {
  const d = parseUtc(ts);
  return d ? d.toLocaleString() : "-";
}

function formatDuration(begin: string | null, end: string | null): string {
  const b = parseUtc(begin);
  const e = parseUtc(end);
  if (!b || !e) return "-";
  const min = Math.round((e.getTime() - b.getTime()) / 60_000);
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)} h ${min % 60} min`;
}

function stateColor(state: string): string {
  if (state === "LIVE") return "#2eb85c";
  if (state.startsWith("PAUSE")) return "#f0a020";
  if (state.startsWith("TEST")) return "#4a90d9";
  return "#899093";
}

const ACTION_LABELS: Record<AdminSimAction["action"], string> = {
  refresh: "Refresh network",
  pause: "Pause SIM",
  resume: "Resume SIM",
  sms: "Command",
};

type Props = { esn: string; iccid: string | null };

const SimCardContent: React.FC<Props> = ({ esn, iccid }) => {
  const [sim, setSim] = useState<AdminSimOverview | null>(null);
  const [actions, setActions] = useState<AdminSimAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsText, setSmsText] = useState("");
  const [smsCommand, setSmsCommand] = useState("");
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessions, setSessions] = useState<AdminSimSession[] | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!iccid) return;
    setLoading(true);
    try {
      const res = await getDeviceSim(esn);
      setSim(res.sim);
      setActions(res.actions);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load SIM");
    } finally {
      setLoading(false);
    }
  }, [esn, iccid]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await getDeviceSimSessions(esn, 20);
      setSessions(res.sessions);
    } catch {
      // toast already shown by api client
    } finally {
      setSessionsLoading(false);
    }
  }, [esn]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => () => {
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
    },
    []
  );

  const runAction = async (key: string, fn: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await fn();
      toast.success(success);
      if (refetchTimer.current) clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => void load(), REFETCH_AFTER_ACTION_MS);
    } catch {
      // toast already shown by api client
    } finally {
      setBusy(null);
      // Refresh the action log right away (the failed attempt is logged too).
      void load();
    }
  };

  const onRefresh = () => {
    if (
      !window.confirm(
        "Refresh network?\n\nForces the carrier to re-register the SIM. The device may drop its connection for a minute."
      )
    )
      return;
    void runAction("refresh", () => refreshDeviceSim(esn), "Network refresh requested");
  };

  const onPause = () => {
    const typed = window.prompt(
      'Pause SIM data?\n\nThe device will stop sending data until resumed. The monthly fee still applies.\n\nType PAUSE to confirm.'
    );
    if (typed?.trim().toUpperCase() !== "PAUSE") return;
    void runAction("pause", () => setDeviceSimState(esn, "pause"), "SIM paused");
  };

  const onResume = () => {
    if (!window.confirm("Resume SIM data?")) return;
    void runAction("resume", () => setDeviceSimState(esn, "live"), "SIM resumed");
  };

  const selectedCommand = SIM_COMMANDS.find((c) => c.code === smsCommand);
  const smsEditable = smsCommand === CUSTOM_COMMAND || Boolean(selectedCommand?.editable);

  const onSelectCommand = (value: string) => {
    setSmsCommand(value);
    const cmd = SIM_COMMANDS.find((c) => c.code === value);
    setSmsText(cmd ? cmd.code : "");
  };

  const onSendSms = () => {
    const text = smsText.trim();
    if (!text) return;
    const label = selectedCommand?.label ?? "custom SMS";
    if (
      (selectedCommand?.confirm || smsCommand === CUSTOM_COMMAND) &&
      !window.confirm(`Send "${label}" (${text}) to this device?`)
    )
      return;
    void runAction(
      "sms",
      async () => {
        await sendDeviceSimSms(esn, text);
        setSmsText("");
        setSmsCommand("");
        setSmsOpen(false);
      },
      `Sent: ${label}`
    );
  };

  const toggleSessions = () => {
    const next = !sessionsOpen;
    setSessionsOpen(next);
    if (next && sessions === null) void loadSessions();
  };

  if (!iccid) return <Muted>No ICCID reported by this device.</Muted>;

  if (!sim) {
    if (loading) return <Spinner />;
    return (
      <Muted>
        No Hologram SIM data{loadError ? ` — ${loadError}` : ""}.{" "}
        <LinkBtn onClick={() => void load()}>Retry</LinkBtn>
      </Muted>
    );
  }

  const paused = sim.state.startsWith("PAUSE");
  const lastAction = actions[0];

  return (
    <div>
      <Row>
        <span>
          <StateDot dotColor={stateColor(sim.state)} />
          <strong>{sim.state}</strong>
        </span>
        <span>
          <Label>Session</Label>
          {sim.openSession ? "open" : "closed"}
        </span>
        <span>
          <Label>APN</Label>
          {sim.apn || "-"}
        </span>
        <span>
          <Label>Hologram IMEI</Label>
          {sim.imei || "-"}
        </span>
        <LinkBtn onClick={() => void load()}>{loading ? "Loading…" : "Reload"}</LinkBtn>
      </Row>
      <Row style={{ marginTop: 8 }}>
        <span>
          <Label>Data this period</Label>
          {formatBytes(sim.usageBytes)}
          {sim.billingPeriodEnd && (
            <Muted as="span" style={{ marginLeft: 6 }}>
              (ends {formatTs(sim.billingPeriodEnd).split(",")[0]})
            </Muted>
          )}
        </span>
      </Row>

      <Actions>
        <Button kind="outline" disabled={busy !== null} onClick={onRefresh}>
          {busy === "refresh" ? "Refreshing…" : "Refresh network"}
        </Button>
        {paused ? (
          <Button kind="outline" disabled={busy !== null} onClick={onResume}>
            {busy === "resume" ? "Resuming…" : "Resume SIM"}
          </Button>
        ) : (
          <Button kind="outline" disabled={busy !== null} onClick={onPause}>
            {busy === "pause" ? "Pausing…" : "Pause SIM"}
          </Button>
        )}
        <Button kind="outline" disabled={busy !== null} onClick={() => setSmsOpen((v) => !v)}>
          Send command…
        </Button>
      </Actions>

      {smsOpen && (
        <SmsBox>
          <select value={smsCommand} onChange={(e) => onSelectCommand(e.target.value)}>
            <option value="" disabled>
              Select a command…
            </option>
            {SIM_COMMANDS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label} — {c.editable ? `${c.code}…` : c.code}
              </option>
            ))}
            <option value={CUSTOM_COMMAND}>Custom text…</option>
          </select>
          {smsEditable && (
            <textarea
              rows={2}
              maxLength={SMS_MAX}
              value={smsText}
              onChange={(e) => setSmsText(e.target.value)}
              placeholder={
                smsCommand === CUSTOM_COMMAND ? "Message to the device" : "Complete the command"
              }
            />
          )}
          <Row>
            <Muted as="span" style={{ margin: 0 }}>
              {smsText ? `SMS body: ${smsText}` : "No command selected"}
              {smsEditable && ` (${smsText.length}/${SMS_MAX})`}
            </Muted>
            <Button
              kind="primary"
              disabled={busy !== null || !smsText.trim()}
              onClick={onSendSms}
            >
              {busy === "sms" ? "Sending…" : "Send"}
            </Button>
          </Row>
        </SmsBox>
      )}

      {lastAction && (
        <Muted>
          Last action: {ACTION_LABELS[lastAction.action]}
          {lastAction.action === "sms" &&
            ` "${commandLabel(lastAction.payload?.body) ?? lastAction.payload?.body ?? ""}"`}{" "}
          · {formatLastOnline(lastAction.createdAt)}{" "}
          · {lastAction.adminEmail} · {lastAction.ok ? "✓" : `failed: ${lastAction.error}`}
        </Muted>
      )}

      <div style={{ marginTop: 16 }}>
        <LinkBtn onClick={toggleSessions}>
          {sessionsOpen ? "▾ Hide recent sessions" : "▸ Show recent sessions"}
        </LinkBtn>
        {sessionsOpen &&
          (sessionsLoading ? (
            <Spinner />
          ) : sessions && sessions.length > 0 ? (
            <TableWrap>
              <table>
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th>Duration</th>
                    <th>Data</th>
                    <th>Network</th>
                    <th>Radio</th>
                    <th>Cell</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, i) => (
                    <tr key={`${s.sessionBegin}-${i}`}>
                      <td>{formatTs(s.sessionBegin)}</td>
                      <td>{formatTs(s.sessionEnd)}</td>
                      <td>{formatDuration(s.sessionBegin, s.sessionEnd)}</td>
                      <td>{formatBytes(s.bytes)}</td>
                      <td>{s.networkName || "-"}</td>
                      <td>{s.radioAccessTechnology || "-"}</td>
                      <td>{s.cellId || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          ) : (
            <Muted>No sessions.</Muted>
          ))}
      </div>
    </div>
  );
};

const DeviceSimCard: React.FC<Props> = (props) => (
  <Card>
    <CardTitle>SIM (Hologram)</CardTitle>
    <SimCardContent {...props} />
  </Card>
);

export default DeviceSimCard;
